import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  FileText,
  Gamepad2,
  Image,
  MonitorPlay,
  Pencil,
  StickyNote,
  FileImage,
  Coins,
  User,
  Shield,
  Users,
  Package,
  Crown,
  CreditCard,
  BookOpen,
  ExternalLink,
  GraduationCap,
  Briefcase,
  Video,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Badge } from '../ui/badge';

const iconMap = {
  Home,
  FileText,
  Gamepad2,
  Image,
  MonitorPlay,
  Pencil,
  StickyNote,
  FileImage,
  Coins,
  User,
  Shield,
  Users,
  Package,
  CreditCard,
  BookOpen,
  Video,
};

const sidebarItems = [
  { id: 'dashboard', name: 'Dashboard', icon: 'Home', path: '/' },
  { id: 'prompts', name: 'Prompts', icon: 'FileText', path: '/prompts' },
  { id: 'playground', name: 'Play Ground', icon: 'Gamepad2', path: '/playground' },
  { id: 'images', name: 'My Images', icon: 'Image', path: '/images' },
  { id: 'videos', name: 'Videos', icon: 'Video', path: '/videos' },
  { id: 'books', name: 'Books', icon: 'BookOpen', path: '/books' },
  { id: 'watermark', name: 'Watermark Remover', icon: 'Pencil', path: '/watermark' },
  { id: 'notepad', name: 'NotePad', icon: 'StickyNote', path: '/notepad' },
  { id: 'converter', name: 'PDF & Image Converter', icon: 'FileImage', path: '/converter' },
  { id: 'credits', name: 'Credits', icon: 'Coins', path: '/credits' },
  { id: 'profile', name: 'Profile', icon: 'User', path: '/profile' },
];

const externalLinks = [
  { id: 'services', name: 'Our Services', icon: Briefcase, url: 'https://connectfirm.com' },
  { id: 'courses', name: 'Courses', icon: GraduationCap, url: 'https://aiformaster.com/courses/' },
];

const adminItems = [
  { id: 'admin-dashboard', name: 'Admin Panel', icon: 'Shield', path: '/admin' },
  { id: 'admin-users', name: 'Manage Users', icon: 'Users', path: '/admin/users' },
  { id: 'admin-prompts', name: 'Manage Prompts', icon: 'FileText', path: '/admin/prompts' },
  { id: 'admin-videos', name: 'Manage Videos', icon: 'Video', path: '/admin/videos' },
  { id: 'admin-books', name: 'Manage Books', icon: 'BookOpen', path: '/admin/books' },
  { id: 'admin-packages', name: 'Manage Packages', icon: 'Package', path: '/admin/packages' },
  { id: 'admin-payments', name: 'Payments & Promos', icon: 'CreditCard', path: '/admin/payments' },
];

const Sidebar = () => {
  const location = useLocation();
  const { user, isAdmin, isPaid } = useAuth();

  const getRoleBadge = () => {
    if (isAdmin) return { text: 'Admin', color: 'bg-red-600' };
    if (isPaid) return { text: 'Premium', color: 'bg-yellow-600' };
    return { text: 'Free', color: 'bg-gray-600' };
  };

  const roleBadge = getRoleBadge();

  return (
    <aside className="fixed left-0 top-[60px] h-[calc(100vh-60px)] w-[180px] bg-[#12121a] border-r border-gray-800 flex flex-col z-40">
      {/* User Welcome Section */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-semibold">
            {user?.avatar || user?.name?.charAt(0) || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-gray-400 text-xs">Welcome back</p>
            <p className="text-white font-medium truncate">{user?.name}</p>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Badge className={`text-xs ${roleBadge.color}`}>
            {isAdmin && <Shield size={10} className="mr-1" />}
            {isPaid && !isAdmin && <Crown size={10} className="mr-1" />}
            {roleBadge.text}
          </Badge>
          <span className="text-gray-500 text-xs">{user?.credits || 0} credits</span>
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 py-2 overflow-y-auto">
        {sidebarItems.map((item) => {
          const IconComponent = iconMap[item.icon];
          const isActive = location.pathname === item.path;

          return (
            <Link
              key={item.id}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg transition-colors duration-200 ${
                isActive
                  ? 'bg-gradient-to-r from-purple-600/30 to-transparent text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
              }`}
            >
              {IconComponent && <IconComponent size={18} />}
              <span className="text-sm">{item.name}</span>
            </Link>
          );
        })}

        {/* External Links Section */}
        <div className="mx-4 my-3 border-t border-gray-800" />
        <div className="px-4 py-2">
          <p className="text-gray-500 text-xs uppercase tracking-wider">Resources</p>
        </div>
        {externalLinks.map((link) => (
          <a
            key={link.id}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800/50 transition-colors"
          >
            <link.icon size={18} />
            <span className="text-sm">{link.name}</span>
            <ExternalLink size={12} className="ml-auto opacity-50" />
          </a>
        ))}

        {/* Admin Section */}
        {isAdmin && (
          <>
            <div className="mx-4 my-3 border-t border-gray-800" />
            <div className="px-4 py-2">
              <p className="text-gray-500 text-xs uppercase tracking-wider">Admin</p>
            </div>
            {adminItems.map((item) => {
              const IconComponent = iconMap[item.icon];
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg transition-colors duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-red-600/30 to-transparent text-red-400'
                      : 'text-gray-400 hover:text-red-400 hover:bg-gray-800/50'
                  }`}
                >
                  {IconComponent && <IconComponent size={18} />}
                  <span className="text-sm">{item.name}</span>
                </Link>
              );
            })}
          </>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
